<template>
  <main-layout controlsBg="light" :hasControlsBar="false" :breadcrumb="breadcrumb">
    <div class="container">
      <grid-row class="margin-top-xl" column-align="center">
        <grid-col col-size="8">
          <div id="header">
            <h1 class="header-title">
              <chevron @click="$router.push({name: 'AnalyticsList'})"/>
              Informe de importación
            </h1>
          </div>
        </grid-col>
      </grid-row>

      <!-- Summary bar -->
      <grid-row class="margin-top-xl" column-align="center" v-if="log">
        <grid-col col-size="8">
          <div class="summary-bar">
            <div class="summary-item">
              <span class="summary-value">{{ log.receivedCount }}</span>
              <span class="summary-label">Extraídos</span>
            </div>
            <div class="summary-arrow">→</div>
            <div class="summary-item saved">
              <span class="summary-value">{{ log.savedCount }}</span>
              <span class="summary-label">Guardados</span>
            </div>
            <div class="summary-arrow">|</div>
            <div class="summary-item skipped">
              <span class="summary-value">{{ log.skippedCount }}</span>
              <span class="summary-label">Omitidos</span>
            </div>
            <div class="summary-arrow">|</div>
            <div class="summary-item rejected">
              <span class="summary-value">{{ log.silentlyRejectedCount }}</span>
              <span class="summary-label">Rechazados (auto)</span>
            </div>
          </div>
        </grid-col>
      </grid-row>

      <!-- Skipped items section -->
      <grid-row class="margin-top-xl" column-align="center" v-if="log && log.skippedItems && log.skippedItems.length > 0">
        <grid-col col-size="8">
          <div class="section">
            <h2 class="section-title">Omitidos ({{ log.skippedItems.length }})</h2>
            <table class="report-table">
              <thead>
                <tr>
                  <th>Nombre extraído</th>
                  <th>Valor</th>
                  <th>Unidad</th>
                  <th>Razón</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in log.skippedItems" :key="'s-' + index"
                    :class="getReasonClass(item.reason)">
                  <td>{{ item.name }}</td>
                  <td>{{ item.value }}</td>
                  <td>{{ item.unit || '-' }}</td>
                  <td>
                    <span class="reason-badge" :class="getReasonClass(item.reason)">
                      {{ formatReason(item.reason) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </grid-col>
      </grid-row>

      <!-- Silently rejected section -->
      <grid-row class="margin-top-xl" column-align="center" v-if="log && log.silentlyRejectedItems && log.silentlyRejectedItems.length > 0">
        <grid-col col-size="8">
          <div class="section">
            <h2 class="section-title">Rechazados automáticamente ({{ log.silentlyRejectedItems.length }})</h2>
            <p class="section-description">Nombres o unidades previamente marcados como no válidos. Se descartan sin procesarse.</p>
            <table class="report-table">
              <thead>
                <tr>
                  <th>Nombre extraído</th>
                  <th>Valor</th>
                  <th>Unidad</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in log.silentlyRejectedItems" :key="'r-' + index" class="rejected-row">
                  <td>{{ item.name }}</td>
                  <td>{{ item.value }}</td>
                  <td>{{ item.unit || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </grid-col>
      </grid-row>

      <!-- No log available -->
      <grid-row class="margin-top-xl" column-align="center" v-if="!log && !loading">
        <grid-col col-size="8">
          <div class="no-data">
            No hay registro de importación para esta analítica.
          </div>
        </grid-col>
      </grid-row>

      <!-- Loading -->
      <grid-row class="margin-top-xl" column-align="center" v-if="loading">
        <grid-col col-size="8">
          <div class="no-data">Cargando...</div>
        </grid-col>
      </grid-row>
    </div>
  </main-layout>
</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import Chevron from '@/components/shared/Chevron';
import api from '../../api/analytics/index';

export default {
  name: 'ImportReport',
  components: {
    MainLayout,
    GridRow,
    GridCol,
    Chevron
  },
  data() {
    return {
      log: null,
      loading: true,
      analyticId: null,
      breadcrumb: [
        {link: '/workspace', name: 'Workspace'},
        {link: '/analytics/list', name: 'Analíticas'},
        {link: '', name: 'Informe de importación', disabled: true}
      ]
    };
  },
  created() {
    this.analyticId = this.$route.params.analyticId;
    this.fetchImportLog();
  },
  methods: {
    fetchImportLog() {
      this.loading = true;
      api.getImportLog(this, this.analyticId)
        .then(response => {
          this.log = response;
          this.loading = false;
        })
        .catch(() => {
          this.log = null;
          this.loading = false;
        });
    },
    formatReason(reason) {
      if (!reason) return '';
      if (reason.includes('INVALID_NUMERIC_VALUE')) return 'Valor no numérico';
      if (reason.includes('NOT_FOUND_OR_AMBIGUOUS')) return 'No encontrado';
      if (reason.includes('INCOMPATIBLE_UNIT')) return 'Unidad incompatible';
      if (reason.includes('DISCARDED_DUPLICATE')) return 'Duplicado descartado';
      return reason;
    },
    getReasonClass(reason) {
      if (!reason) return '';
      if (reason.includes('INVALID_NUMERIC_VALUE')) return 'reason-invalid';
      if (reason.includes('NOT_FOUND_OR_AMBIGUOUS')) return 'reason-not-found';
      if (reason.includes('INCOMPATIBLE_UNIT')) return 'reason-unit';
      if (reason.includes('DISCARDED_DUPLICATE')) return 'reason-duplicate';
      return '';
    }
  }
};
</script>

<style scoped lang="scss">
.summary-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: #ffffff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 16px;
  border-radius: 8px;
  background: #f5f5f5;

  &.saved {
    background: #e8f5e9;
    .summary-value { color: #2e7d32; }
  }

  &.skipped {
    background: #fff3e0;
    .summary-value { color: #e65100; }
  }

  &.rejected {
    background: #fce4ec;
    .summary-value { color: #c62828; }
  }
}

.summary-value {
  font-size: 28px;
  font-weight: bold;
  color: #333;
}

.summary-label {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.summary-arrow {
  color: #ccc;
  font-size: 20px;
}

.section {
  background: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.section-title {
  font-size: 18px;
  color: #333;
  margin-bottom: 12px;
}

.section-description {
  font-size: 13px;
  color: #888;
  margin-bottom: 16px;
}

.report-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
  }

  th {
    background-color: #fafafa;
    color: #555;
    font-weight: bold;
    font-size: 13px;
  }

  td {
    font-size: 14px;
  }
}

.reason-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  &.reason-invalid {
    background: #e0e0e0;
    color: #616161;
  }

  &.reason-not-found {
    background: #ffcdd2;
    color: #c62828;
  }

  &.reason-unit {
    background: #fff9c4;
    color: #f57f17;
  }

  &.reason-duplicate {
    background: #ffe0b2;
    color: #e65100;
  }
}

.rejected-row {
  td {
    color: #999;
    font-style: italic;
  }
}

.no-data {
  text-align: center;
  padding: 40px 20px;
  color: #888;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/analytics/ImportReport.vue